import React from 'react';
import PropTypes from 'prop-types';
import {List, Map} from 'immutable';
import './UpcomingCoachingAgenda.styl';
import connectToStores from 'alt-utils/lib/connectToStores';
import UpcomingReviewsStore from 'stores/coaching/UpcomingReviewsStore';
import AuthStore from 'stores/AuthStore';
import UpcomingReviewsActions, {
  REVIEW_COUNT_FOR_SIDEBAR,
} from 'actions/coaching/UpcomingReviewsActions';
import CoachingReviewStore from 'stores/coaching/CoachingReviewStore';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import ModifyReviewDialogContainer from 'components/coaching/ModifyReviewDialogContainer';
import UpcomingAgendaCard from 'components/dashboard/UpcomingAgendaCard';
import {Link} from 'react-router';
import Button from '@material-ui/core/Button';
import UserStore from 'stores/UserStore';
import UpcomingAgendaEmpty from 'components/dashboard/UpcomingAgendaEmpty';
import buildUrl from 'components/dashboard/buildUrl';
import User from 'records/User';
import CoachingReviewTypeStore, {typesAndCategories} from 'stores/coaching/CoachingReviewTypeStore';
import {getQueryParam} from 'utils/getQueryParam';
import UPCOMING_REVIEW_ROLES from 'actions/coaching/UPCOMING_REVIEW_ROLES';

export function loadUpcomingReviewData(managerId, coachId, reviewRole) {
  // if loading for a coach, gather reviews where they're the recipient
  // and where they're the coach. this way they can interact with plans
  // where they're the recipient.
  const loadForCoach = !!coachId;
  const userId = coachId || managerId || AuthStore.getState().user.id;
  const notForCoachReviewRole = reviewRole || UPCOMING_REVIEW_ROLES.COACH;

  setTimeout(async () => {
    const [, reviews] = await UpcomingReviewsActions.upcomingReviews(
      userId,
      loadForCoach ? UPCOMING_REVIEW_ROLES.ALL : notForCoachReviewRole
    );
    for (const review of reviews) {
      CoachingReviewActions.fetchReviewFeedback(review.id);
    }
  }, 0);
}

@connectToStores
export default class UpcomingCoachingAgenda extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      reviewToModify: undefined,
    };
    this.refreshCoachingData = this.refreshCoachingData.bind(this);
    this.onSetReviewType = this.onSetReviewType.bind(this);
  }

  static getStores() {
    return [
      CoachingReviewStore,
      UpcomingReviewsStore,
      AuthStore,
      UserStore,
      CoachingReviewTypeStore,
    ];
  }

  static getPropsFromStores(props) {
    const sessionUser = AuthStore.getState().user;

    const [, reviewTypes] = typesAndCategories(CoachingReviewTypeStore);

    return {
      sessionUser,
      coach: UserStore.getState().users.get(
        // when a higher-rank user looks at a lower-rank user's dash,
        // it is the lower-rank user's agenda that we show
        props.coachId || props.managerId || sessionUser.id
      ),
      reviewFileUploads: CoachingReviewStore.getState().reviewFileUploads,
      reviews: UpcomingReviewsStore.getState().upcomingReviewsMap.get(
        props.coachId || props.managerId || sessionUser.id,
        List()
      ),
      currentlyFillingOutReviewId: CoachingReviewStore.getState().currentlyFillingOutReviewId,
      reviewTypes,
    };
  }

  componentDidMount() {
    const {managerId, coachId} = this.props;
    loadUpcomingReviewData(managerId, coachId);
  }

  componentDidUpdate(prevProps) {
    if (
      (this.props.coach && !prevProps.coach) ||
      (this.props.coach && prevProps.coach && this.props.coach.id !== prevProps.coach.id)
    ) {
      const {managerId, coachId} = this.props;
      loadUpcomingReviewData(managerId, coachId);
    }
  }

  refreshCoachingData() {
    const {managerId, coachId} = this.props;
    loadUpcomingReviewData(managerId, coachId);
    this.props.refreshCoachingData();
  }

  onSetReviewType(review, reviewTypeId) {
    CoachingReviewActions.updateReview(review.set('reviewTypeId', reviewTypeId)).then(() =>
      this.refreshCoachingData()
    );
  }

  render() {
    const {
      coach,
      sessionUser,
      reviews,
      currentlyFillingOutReviewId,
      reviewFileUploads,
      adminId,
      managerId,
      coachId,
      reviewTypes,
      location: {pathname},
    } = this.props;
    const {reviewToModify} = this.state;
    const otherUserView = coach && coach.id !== sessionUser.id;

    if (reviews.isEmpty()) {
      return (
        <UpcomingAgendaEmpty
          refreshCoachingData={this.refreshCoachingData}
          coach={coach}
          sessionUser={sessionUser}
          otherUserView={otherUserView}
          adminId={adminId}
          managerId={managerId}
          coachId={coachId}
        />
      );
    }

    const hideLink =
      pathname.endsWith('/coaching-plans') &&
      // if exec - hide the link only on their team dash, not on exec coaching
      (adminId ? managerId : true);

    // If an admin is viewing someone else's agenda, we need to include a managerId
    const showMoreDestination = otherUserView
      ? buildUrl({...this.props, managerId: coach.id}) + '/coaching-plans'
      : buildUrl(this.props) + '/coaching-plans';

    const activePageReviewId = parseInt(getQueryParam(location, 'reviewId'), 10);

    // Render button when an admin user is viewing the admin dash
    return (
      <div className="upcoming-coaching-agenda">
        <h4>{otherUserView ? `${coach.firstName}'s` : 'My'} Coaching Agenda</h4>
        <section className="upcoming-cards">
          {reviews
            .toList()
            .filter(r => r.id !== activePageReviewId && r.id !== currentlyFillingOutReviewId)
            .take(REVIEW_COUNT_FOR_SIDEBAR)
            .map(r => (
              <UpcomingAgendaCard
                sessionUser={sessionUser}
                coach={coach}
                otherUserView={otherUserView}
                key={r.id}
                reviewFileUploads={reviewFileUploads.get(r.id)}
                openModifyDialog={review => this.setState({reviewToModify: review})}
                review={r}
                reviewTypes={reviewTypes}
                onSetReviewType={this.onSetReviewType}
                coachId={coachId}
                managerId={managerId}
                adminId={adminId}
                fromPath={pathname}
              />
            ))}
        </section>
        {!hideLink && (
          <Button
            variant="text"
            fullWidth={false}
            component={Link}
            className="generic-link pointer"
            to={showMoreDestination}>
            Show more
          </Button>
        )}
        <ModifyReviewDialogContainer
          open={!!reviewToModify}
          refreshCoachingData={this.refreshCoachingData}
          onRequestClose={() => this.setState({reviewToModify: undefined})}
          review={reviewToModify}
        />
      </div>
    );
  }
}

UpcomingCoachingAgenda.propTypes = {
  adminId: PropTypes.string,
  managerId: PropTypes.string,
  coachId: PropTypes.string,
  coach: PropTypes.instanceOf(User),
  sessionUser: PropTypes.instanceOf(User),
  currentlyFillingOutReviewId: PropTypes.bool,
  location: PropTypes.object, // React Router location
  reviewFileUploads: PropTypes.instanceOf(Map), // Map<ReviewId, List<ReviewFileUpload>>
  reviews: PropTypes.instanceOf(List), // List<CoachingReview> (UpcomingReview ver)
};
